import { useCallback, useEffect, useRef, useState } from 'react'

import { CHG } from 'tools/Input3/reducer'
import { useWait } from 'tools/Loading'
import { Label } from 'tools/Uniform'
// import notifier from 'tools/Notify'
import { stopEvent } from 'utils/event'
import { noop } from 'utils/function'

function PlaceholderInput(props) {
  const wait = useWait(1000)

  if (!wait) return <span />
  return (
    <InnerInput
      {...props}
      onSave={noop}
      onChange={noop}
      placeholder={'loading…'}
    />
  )
}

export function Input({ loading = false, ...props }) {
  if (loading) return <PlaceholderInput {...props} />
  return <InnerInput {...props} />
}

function statusToClass(status) {
  switch (status) {
    case CHG.DIRTY:
      return 'dirty'
    case CHG.FAILED:
      return 'failed'
    default:
      return ''
  }
}

function calculateHeight(elem, multiline, lineheight) {
  if (multiline) {
    const MIN_HEIGHT = 30
    const origHeight = elem.style.height
    // this is a little game to trick the scrollHeight to be as tight as possible
    elem.style.height = 'auto'
    const newHeight = Math.max(MIN_HEIGHT, elem.scrollHeight)
    elem.style.height = origHeight
    return newHeight // * lineheight
  } else {
    return 'auto'
  }
}

function InnerInput({
  // onError = noop,
  value,
  onSave = (props, value, good, bad) => {},
  onChange: changer,
  onCancel,
  style = {},
  label,
  labelClass,
  info,
  markdown = false,
  help,
  helpClass = 'f3 mb3 gray',
  wrapClass = 'flex-grow-1',
  wrap2Class = 'flex-grow-1',
  className = '',
  multiline = false,
  lineheight = 1,
  placeholder = '',
  makeVars,
  minRows = 1,
  defaultIcon,
  saveButton,
  showError = true,
  children,
  debug = undefined,
  ...args
}) {
  // const [, notifyD] = useContext(GlobalContext)
  const ref = useRef(null)
  value = value ?? ''
  const [state, setState] = useState({
    value: value,
    prev: value,
    style: style,
    status: CHG.OK,
    cancel: false,
    error: undefined
  })

  useEffect(() => {
    const textarea = ref?.current
    if (textarea && multiline) {
      setState((state) => ({
        ...state,
        style: {
          ...state.style,
          height: calculateHeight(textarea, multiline, lineheight)
        }
      }))
    }
  }, [ref, multiline, lineheight])

  const unchanged = state.value === state.prev
  const save = useCallback(
    (e) => {
      if (unchanged) {
        setState((s) => ({ ...s, status: CHG.OK }))
        return
      }
      const textarea = e.currentTarget
      return new Promise((resolve, reject) => {
        return onSave(
          { ...args, makeVars, dirty: unchanged, event: e },
          state.value,
          resolve,
          reject
        )
      })
        .then((status = CHG.SAVED, ...rest) => {
          setState((s) => ({
            ...s,
            status,
            prev: s.value,
            error: undefined,
            style: {
              ...s.style,
              height: calculateHeight(textarea, multiline, lineheight)
            }
          }))
          if (status === CHG.SAVED) {
            // TODO: setup an unmount for this
            setTimeout(() => {
              setState((s) => ({ ...s, status: CHG.OK }))
            }, 1000)
          }
        })
        .catch((errmsg) => {
          setState((s) => ({ ...s, error: errmsg, status: CHG.FAILED }))
        })
    },
    [
      state.value,
      unchanged,
      onSave,
      args, // guh, this will cause a re-render every time
      setState,
      makeVars,
      multiline,
      lineheight
    ]
  )

  const onChange = useCallback(
    (e) => {
      const textarea = e.target
      const value = textarea.value
      setState((state) => ({
        ...state,
        value,
        style: {
          ...state.style,
          height: calculateHeight(textarea, multiline, lineheight)
        },
        cancel: false,
        status: CHG.DIRTY
      }))
      // notifier.editing(notifyD)
      changer && changer(value, args)
    },
    [setState, changer, args, multiline, lineheight]
  )

  const onFocus = useCallback(
    (e) => {
      const textarea = e.target
      setState((state) => ({
        ...state,
        cancel: false,
        style: {
          ...state.style,
          height: calculateHeight(textarea, multiline, lineheight)
        }
      }))
    },
    [multiline, lineheight]
  )

  const onKeyDown = useCallback(
    (ev) => {
      if (!multiline && ev.keyCode === 13 && !ev.shiftKey) {
        stopEvent(ev)
        return save(ev)
      }
    },
    [multiline, save]
  )

  const status = statusToClass(state.status)
  if (debug) console.log(`<Input2 ${debug}>`, { state, status })

  return (
    <>
      {label && (
        <Label
          markdown={markdown}
          children={label}
          info={info}
          className={labelClass}
          maxLength={args.maxLength}
          value={state.value}
          dirty={state.status}
          help={help}
          helpClass={helpClass}
        />
      )}
      <div className={wrapClass + ' flex-items'}>
        <div className={wrap2Class + ' input-inline relative'}>
          <textarea
            className={`${className} ${status}`}
            value={state.value}
            ref={ref}
            rows={minRows}
            style={state.style}
            onKeyDown={onKeyDown}
            onBlur={saveButton ? () => {} : save}
            onChange={onChange}
            onFocus={onFocus}
            placeholder={placeholder}
            {...args}
          />
          <ShowIcon
            status={state.status}
            setOk={() => setState({ ...state, status: CHG.OK })}
            defaultIcon={defaultIcon}
          />
          {saveButton && state.status === CHG.DIRTY && !state.cancel && (
            <div className="flex absolute right-0 z-9999 mt1 gh2">
              <SaveButton
                save={() => {
                  setState((s) => ({ ...s, cancel: true }))
                  onCancel && onCancel(false)
                }}
                label={<i className="fas fa-times" />}
                className="neutral"
                xref={ref}
              />
              <SaveButton
                save={save}
                label={
                  saveButton !== true ? (
                    saveButton
                  ) : (
                    <i className="fas fa-check" />
                  )
                }
                xref={ref}
              />
            </div>
          )}
        </div>
        {children}
      </div>
      {state.error && <div className="error f2 mt2">{state.error}</div>}
    </>
  )
}

function ShowIcon({ setOk, status, defaultIcon }) {
  switch (status) {
    case CHG.FAILED:
      return (
        <div onClick={setOk} className="mark error center-y pa1 br2">
          <i className="red fas fa-times" />
        </div>
      )
    case CHG.SAVED:
      return (
        <div onClick={setOk} className="mark ok center-y pa1 br2">
          <i className="green fas fa-check" />
        </div>
      )
    default:
      return defaultIcon ? (
        <div className="mark ok center-y pa1 br2">{defaultIcon}</div>
      ) : null
  }
}

function SaveButton({ save, label, xref, className = '' }) {
  return (
    <button
      onClick={(e) => {
        e.currentTarget = xref.current
        e.target = xref.current
        save(e)
      }}
      className={className}
    >
      {label}
    </button>
  )
}

export { CHG }
export default Input
